import { Link } from "@tanstack/react-router";
import { isActive, useRuns } from "@/app/gradingQueries";
import { useMySubmissions } from "@/app/reviewQueries";
import { useServerCheck, useSubmitFlow } from "@/routes/check/hooks";
import { STATUS_LABEL } from "@/routes/submission/model";
import { Button } from "@/ui/Button";

/** Низ боковой панели S5: «проверить на сервере» (сначала сохраняется черновик),
 *  статус последнего прогона со ссылкой в S6 и «отправить на проверку» —
 *  можно ли и почему нет решает сервер (правило 3). */
export function ServerSubmit({ assignmentId, flush }: { assignmentId: string; flush: () => Promise<boolean> }) {
  const runs = useRuns(assignmentId);
  const check = useServerCheck(assignmentId, flush);
  const submit = useSubmitFlow(assignmentId);
  const mine = useMySubmissions(assignmentId);
  const last = runs.data?.[0];
  const running = isActive(last);
  const lastSub = mine.data?.[0];

  return (
    <div className="flex flex-col gap-2 border-t border-bd bg-bg2 px-4 py-3.5">
      {last && (
        <Link to="/notebook/$assignmentId/check" params={{ assignmentId }} search={{ run: last.id }} className="font-mono text-[11px] text-ink2 underline-offset-2 hover:underline" data-testid="last-run">
          {running ? `прогон #${last.n} идёт…` : last.status === "error" || last.result?.env_error ? `прогон #${last.n} · ошибка среды` : `прогон #${last.n} · ${last.passed}/${last.total}`} →
        </Link>
      )}
      <Button className="w-full justify-center" onClick={() => void check.run()} disabled={check.busy || running} reason={check.busy ? "сохраняем черновик и ставим прогон…" : running ? "прогон уже идёт" : undefined} data-testid="server-check">
        проверить на сервере
      </Button>
      {check.error && (
        <p className="m-0 font-mono text-[11px] leading-[1.5] text-err" role="alert">
          {check.error}
        </p>
      )}
      {(submit.submitted || lastSub) && (
        <Link to="/notebook/$assignmentId/review" params={{ assignmentId }} className="font-mono text-[11px] text-acc underline-offset-2 hover:underline" data-testid="to-review">
          {lastSub && !submit.submitted ? `сдача v${lastSub.iteration} · ${STATUS_LABEL[lastSub.status]} · ` : ""}к ревью →
        </Link>
      )}
      {submit.submitted ? (
        <p className="m-0 font-mono text-[11px] leading-[1.5] text-ok" data-testid="submitted">
          Отправлено ментору · на проверке
        </p>
      ) : (
        <>
          <Button variant={submit.eligible ? "primary" : undefined} className="w-full justify-center" data-testid="submit" disabled={!submit.eligible || submit.sending} onClick={() => void submit.send()}>
            отправить на проверку
          </Button>
          {!submit.eligible && (
            <p className="m-0 font-mono text-[11px] leading-[1.5] text-ink2" data-testid="submit-reason">
              {submit.reason}
            </p>
          )}
          {submit.error && (
            <p className="m-0 font-mono text-[11px] leading-[1.5] text-err" role="alert">
              {submit.error}
            </p>
          )}
        </>
      )}
    </div>
  );
}
